import type {UserEventSpec, UserEventStruct} from '@cohort/shared/schema/apps/userEvent';
import {PositiveIntegerSchema} from '@cohort/shared/schema/common';
import {z} from 'zod';

export const AppProfileSchema = z.object({
  id: z.string().nullable(),
  username: z.string().nullable(),
});
export type AppProfile = z.infer<typeof AppProfileSchema>;

export const AppProfileWithIdSchema = z.object({
  id: z.string(),
  username: z.string().nullable(),
});
export type AppProfileWithId = z.infer<typeof AppProfileWithIdSchema>;

export type ConnectorType = 'oauth1' | 'oauth2' | 'custom';

export type ConnectorStruct<
  Id extends string = string,
  AppCredentials = unknown,
  Credentials = unknown,
  Profile extends AppProfile = AppProfile,
  ConnectionConfig = unknown,
> = {
  Id: Id;
  AppCredentials: AppCredentials;
  Credentials: Credentials;
  Profile: Profile;
  ConnectionConfig: ConnectionConfig;
};

export type AppStruct<
  Id extends string = string,
  MerchantConnector extends ConnectorStruct | null = ConnectorStruct | null,
  UserConnector extends ConnectorStruct | null = ConnectorStruct | null,
  UsersSyncIntegration extends
    UsersSyncIntegrationStruct | null = UsersSyncIntegrationStruct | null,
  ActionIds extends string = string,
  NotificationIntegrationIds extends string = string,
  PerkIntegrationIds extends string = string,
  TriggerIntegrationIds extends string = string,
  EventIds extends string = string,
  Teaser extends boolean | undefined = boolean | undefined,
> = {
  Id: Id;
  MerchantConnector: MerchantConnector;
  UserConnector: UserConnector;
  UsersSyncIntegration: UsersSyncIntegration;
  ActionIds: ActionIds;
  NotificationIntegrationIds: NotificationIntegrationIds;
  PerkIntegrationIds: PerkIntegrationIds;
  TriggerIntegrationIds: TriggerIntegrationIds;
  EventIds: EventIds;
  Teaser: Teaser;
};

export type ActionStruct<Id extends string = string, Input = unknown, Output = unknown> = {
  Id: Id;
  Input: Input;
  Output: Output;
};

export const AsyncActionRetryOptionsSchema = z.object({
  maxAttempts: PositiveIntegerSchema,
  delayMs: PositiveIntegerSchema,
});
export type AsyncActionRetryOptions = z.infer<typeof AsyncActionRetryOptionsSchema>;

export type ActionSpec<T extends ActionStruct = ActionStruct> = {
  readonly id: T['Id'];
  readonly inputSchema: z.ZodSchema<T['Input']>;
  readonly outputSchema: z.ZodSchema<T['Output']>;
};

export type PerkIntegrationStruct<
  Id extends string = string,
  Config = unknown,
  State = unknown,
  AccessData = unknown,
  UsageInputData = unknown,
  InternalUsageData = unknown,
  ExposedUsageData = InternalUsageData,
> = {
  Id: Id;
  Config: Config;
  State: State;
  AccessData: AccessData;
  UsageInputData: UsageInputData;
  InternalUsageData: InternalUsageData;
  ExposedUsageData: ExposedUsageData;
};

export type PerkIntegrationSpec<T extends PerkIntegrationStruct = PerkIntegrationStruct> = {
  id: T['Id'];
  configSchema: z.ZodSchema<T['Config']>;
  stateSchema: z.ZodSchema<T['State']>;
  accessDataSchema: z.ZodSchema<T['AccessData']>;
  usageInputDataSchema: z.ZodSchema<T['UsageInputData']>;
  internalUsageDataSchema: z.ZodSchema<T['InternalUsageData']>;
  exposedUsageDataSchema: z.ZodSchema<T['ExposedUsageData']>;
  defaultMaxUsagesPerToken: number | null;
};

export type UsersSyncIntegrationStruct<Config = unknown> = {
  Id: string;
  Config: Config;
};

export type UsersSyncIntegrationSpec<
  T extends UsersSyncIntegrationStruct = UsersSyncIntegrationStruct,
> = {
  id: T['Id'];
  configSchema: z.ZodSchema<T['Config']>;
};

export type ConnectorSpec<T extends ConnectorStruct = ConnectorStruct> = {
  id: T['Id']; // This can be different from the app type, for example instagram merchant connection uses Facebook
  type: ConnectorType;
  credentialsSchema: z.ZodSchema<T['Credentials']>;
  appCredentialsSchema: z.ZodSchema<T['AppCredentials']> | null;
  profileSchema: z.ZodSchema<T['Profile']> | null;
  connectionConfigSchema: z.ZodSchema<T['ConnectionConfig']> | null;
};

export type Credentials<T extends ConnectorStruct = ConnectorStruct> = {
  appCredentials: T['AppCredentials'];
  userCredentials: T['Credentials'];
  connectionConfig: T['ConnectionConfig'] | null;
};

export type MerchantCredentials<T extends AppStruct> =
  T['MerchantConnector'] extends ConnectorStruct ? Credentials<T['MerchantConnector']> : null;

export type UserCredentials<T extends AppStruct> = T['UserConnector'] extends ConnectorStruct
  ? Credentials<T['UserConnector']>
  : null;

export type MerchantConnectorSpec<T extends AppStruct> = T['MerchantConnector'] extends null
  ? null
  : ConnectorSpec<NonNullable<T['MerchantConnector']>>;

export type UserConnectorSpec<T extends AppStruct> = T['UserConnector'] extends null
  ? null
  : ConnectorSpec<NonNullable<T['UserConnector']>>;

export type AppSpec<T extends AppStruct = AppStruct> = {
  readonly id: T['Id'];
  readonly name: string;
  readonly merchantConnector: MerchantConnectorSpec<T>;
  readonly userConnector: UserConnectorSpec<T>;
  readonly usersSyncSpec: T['UsersSyncIntegration'] extends null
    ? null
    : UsersSyncIntegrationSpec<NonNullable<T['UsersSyncIntegration']>>;
  readonly actionSpecs: ReadonlyArray<ActionSpec<ActionStruct<T['ActionIds']>>>;
  readonly notificationIntegrationSpecs: ReadonlyArray<
    NotificationIntegrationSpec<NotificationIntegrationStruct<T['NotificationIntegrationIds']>>
  >;
  readonly perkIntegrationSpecs: ReadonlyArray<
    PerkIntegrationSpec<PerkIntegrationStruct<T['PerkIntegrationIds']>>
  >;
  readonly triggerIntegrationSpecs: ReadonlyArray<
    TriggerIntegrationSpec<TriggerIntegrationStruct<T['TriggerIntegrationIds']>>
  >;
  readonly userEventSpecs: ReadonlyArray<UserEventSpec<UserEventStruct<T['EventIds']>>>;
};

export type TriggerIntegrationStruct<
  Id extends string = string,
  Config = unknown,
  VerificationAttemptData = unknown,
  VerificationAttemptInput = unknown,
> = {
  Id: Id;
  Config: Config;
  VerificationAttemptData: VerificationAttemptData;
  VerificationAttemptInput: VerificationAttemptInput;
};

export type TriggerIntegrationType = 'action' | 'verification';

export type TriggerIntegrationSpec<T extends TriggerIntegrationStruct = TriggerIntegrationStruct> =
  {
    id: T['Id'];
    type: TriggerIntegrationType;
    configurableMaxAttempts: boolean;
    configSchema: z.ZodSchema<T['Config']>;
    verificationAttemptDataSchema: z.ZodSchema<T['VerificationAttemptData']>;
    verificationAttemptInputSchema: z.ZodSchema<T['VerificationAttemptInput']>;
  };

export type NotificationIntegrationStruct<
  Id extends string = string,
  Config = unknown,
  NotificationData = unknown,
  NotificationInput = unknown,
  EventIds extends string = string,
> = {
  Id: Id;
  Config: Config;
  NotificationData: NotificationData;
  NotificationInput: NotificationInput;
  EventIds: EventIds;
};

export type NotificationIntegrationSpec<
  T extends NotificationIntegrationStruct = NotificationIntegrationStruct,
> = {
  id: T['Id'];
  configSchema: z.ZodSchema<T['Config']>;
  notificationDataSchema: z.ZodSchema<T['NotificationData']>;
  notificationInputSchema: z.ZodSchema<T['NotificationInput']>;
};

export type AppStructWithUserConnector<T extends AppStruct = AppStruct> = T & {
  readonly UserConnector: NonNullable<T['UserConnector']>;
};

export type AppStructWithMerchantConnector<T extends AppStruct = AppStruct> = T & {
  readonly MerchantConnector: NonNullable<T['MerchantConnector']>;
};

export type AppSpecWithUserConnector<
  T extends AppStructWithUserConnector = AppStructWithUserConnector,
> = Omit<AppSpec<T>, 'userConnector'> & {
  readonly userConnector: NonNullable<UserConnectorSpec<T>>;
};

export type AppSpecWithMerchantConnector<
  T extends AppStructWithMerchantConnector = AppStructWithMerchantConnector,
> = Omit<AppSpec<T>, 'merchantConnector'> & {
  readonly merchantConnector: NonNullable<MerchantConnectorSpec<T>>;
};
